import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FINAL_CTA, CONTACT } from "@/data/content";
import { CTAButton } from "@/components/landing/shared";
import { CheckGlyph } from "@/components/landing/icons";
import { leadSchema, emptyLead, describeSubmitError } from "@/components/landing/leadSchema";
import { submitLead } from "@/lib/api";
import { EASE } from "@/lib/motion";

const inputCls =
  "h-12 rounded-xl border-[color:var(--hairline)] bg-[color:var(--ivory)] px-4 text-[color:var(--ink)] placeholder:text-[#8B96A1] focus-visible:ring-2 focus-visible:ring-[color:var(--blue)] focus-visible:ring-offset-0 focus-visible:border-[color:var(--blue)]";

const STATUS = { idle: "idle", submitting: "submitting", success: "success", error: "error" };
const DEFAULT_INTEREST = "engagement_assessment";

const Field = ({ id, label, error, children, optional }) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id} className="flex items-center justify-between text-[0.85rem] font-medium text-[color:var(--ink)]">
      {label}
      {optional ? <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#7A8794]">Optional</span> : null}
    </Label>
    {children}
    {error ? (
      <p role="alert" className="text-[0.8rem] text-[#B42318]" data-testid={`${id}-error`}>
        {error}
      </p>
    ) : null}
  </div>
);

const TextField = ({ id, name, label, register, errors, optional, ...inputProps }) => (
  <Field id={id} label={label} optional={optional} error={errors[name]?.message}>
    <Input id={id} className={inputCls} data-testid={`lead-capture-${name}-input`} aria-invalid={!!errors[name]} {...inputProps} {...register(name)} />
  </Field>
);

const Honeypot = ({ register }) => (
  <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
    <label htmlFor="lead-website">Website</label>
    <input id="lead-website" type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
  </div>
);

const SuccessState = ({ result, onReset }) => (
  <motion.div
    key="success"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4, ease: EASE }}
    className="flex min-h-[420px] flex-col justify-center"
    data-testid="lead-capture-success"
    role="status"
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--navy)] text-[color:var(--gold)]">
      <CheckGlyph />
    </span>
    <h3 className="font-display mt-6 text-[1.9rem] leading-[1.1] tracking-[-0.02em] text-[color:var(--ink)]">
      Thank you{result?.name ? `, ${result.name.split(" ")[0]}` : ""}.
    </h3>
    <p className="mt-3 text-[color:var(--muted-ink)]">
      {result?.message || "An HR Anexi consultant will be in touch shortly."}
      {result?.email ? (
        <>
          {" "}We will reach you at <span className="font-medium text-[color:var(--ink)]">{result.email}</span>.
        </>
      ) : null}
    </p>
    <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-[#7A8794]">Reference {result?.id?.slice(0, 8)}</p>
    <button
      type="button"
      onClick={onReset}
      className="mt-8 self-start text-[0.9rem] font-medium text-[color:var(--navy)] underline-offset-4 hover:underline"
      data-testid="lead-capture-reset-button"
    >
      Send another request
    </button>
  </motion.div>
);

const SubmitRow = ({ submitting }) => (
  <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <CTAButton type="submit" variant="primary" size="lg" icon={submitting ? null : "up"} disabled={submitting} testId="lead-capture-submit-button" className="w-full sm:w-auto">
      {submitting ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
        </span>
      ) : (
        "Start the conversation"
      )}
    </CTAButton>
    <p className="text-[0.8rem] text-[#7A8794]">Your details are only used to respond to this request.</p>
  </div>
);

const SubmitError = () => (
  <p role="alert" className="mt-5 rounded-xl border border-[#F2C7C2] bg-[#FDF3F2] px-4 py-3 text-[0.9rem] text-[#B42318]" data-testid="lead-capture-error">
    We couldn't send your request. Please try again, or email us at{" "}
    <a href={`mailto:${CONTACT.email}`} className="font-medium underline underline-offset-2">
      {CONTACT.email}
    </a>
    .
  </p>
);

/** Encapsulates form state, validation and submission to /api/leads. */
const useLeadForm = () => {
  const [status, setStatus] = useState(STATUS.idle);
  const [result, setResult] = useState(null);
  const form = useForm({ resolver: zodResolver(leadSchema), defaultValues: emptyLead(DEFAULT_INTEREST) });
  const { reset } = form;

  const submit = async (values) => {
    setStatus(STATUS.submitting);
    try {
      const data = await submitLead({ ...values, source: "ees-landing" });
      setResult({ ...data, name: values.name, email: values.email });
      setStatus(STATUS.success);
      toast.success("Request received", { description: "An HR Anexi consultant will be in touch shortly." });
    } catch (err) {
      setStatus(STATUS.error);
      toast.error("Could not send your request", { description: describeSubmitError(err) });
    }
  };

  const restart = () => {
    reset();
    setStatus(STATUS.idle);
  };

  return { form, status, result, submit, restart };
};

export const LeadForm = () => {
  const { form, status, result, submit, restart } = useLeadForm();
  const { register, handleSubmit, formState: { errors } } = form;

  return (
    <div className="card-premium relative overflow-hidden p-6 sm:p-8" data-testid="lead-capture-card">
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-[color:var(--gold)]" />
      <AnimatePresence mode="wait">
        {status === STATUS.success ? (
          <SuccessState result={result} onReset={restart} />
        ) : (
          <motion.form key="form" initial={false} exit={{ opacity: 0 }} onSubmit={handleSubmit(submit)} noValidate data-testid="lead-capture-form">
            <p className="eyebrow text-[color:var(--muted-ink)]">{FINAL_CTA.formTitle}</p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <TextField id="lead-name" name="name" label="Full name" autoComplete="name" placeholder="Your name" register={register} errors={errors} />
              <TextField id="lead-company" name="company" label="Organization" autoComplete="organization" placeholder="Company name" register={register} errors={errors} />
              <TextField id="lead-role" name="role" label="Role" autoComplete="organization-title" placeholder="e.g. CHRO, CEO, Business Head" register={register} errors={errors} />
              <TextField id="lead-email" name="email" label="Work email" type="email" autoComplete="email" placeholder="you@company.com" register={register} errors={errors} />
              <div className="sm:col-span-2">
                <TextField id="lead-phone" name="phone" label="Phone" type="tel" autoComplete="tel" placeholder="+91" optional register={register} errors={errors} />
              </div>
              <Honeypot register={register} />
            </div>

            {status === STATUS.error ? <SubmitError /> : null}
            <SubmitRow submitting={status === STATUS.submitting} />
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
